import { ActionIcon, Menu, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconBlocks,
  IconBug,
  IconCopyPlus,
  IconDotsVertical,
  IconComponents,
  IconPlayerPlayFilled,
  IconStrikethrough,
  IconTrash,
} from '@tabler/icons-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { previewExecutionIdAtom } from '~/atoms/atoms';
import { erroredOperationAtom, selectedStepsIdsAtom } from '~/atoms/no-code-atoms';

import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { api } from '~/trpc-extra/api';
import StepMode from './StepMode';
import { modals } from '@mantine/modals';
import { usePreviewFromLocation } from '~/hooks/vm/usePreviewFromLocation';
import { useDeleteSteps } from '~/hooks/mutations/steps/useDeleteSteps';
import { useDuplicateStep } from '~/hooks/mutations/steps/useDuplicateStep';
import { useStepOpsToSteps } from '~/hooks/mutations/steps/useStepOpsToSteps';
import { isStepSeparating } from '~/hooks/mutations/optimistic-handles';
import { CreateComponentConfirmationDialog } from './CreateComponentConfirmationDialog';

const ICON_SIZE = 15;

export default function StepSettingsMenuButton({
  step,
  testContentId,
  newStepOnMoveCursor,
  isBeforeCursor,
  disabled,
  readonly,
}: {
  step: SelectedStep;
  testContentId: string;
  newStepOnMoveCursor: boolean;
  isBeforeCursor: boolean;
  disabled?: boolean;
  readonly?: boolean;
}) {
  const trpcUtils = api.useUtils();
  const setSelectedStepsIds = useSetAtom(selectedStepsIdsAtom);
  const { isAdmin } = useAuthData();
  const previewExecutionId = useAtomValue(previewExecutionIdAtom);

  const { data: testContent } = api.testContent.get.useQuery(
    { id: testContentId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testContentId },
  );

  const { data: projectAutoHealConfig } = api.project.getProjectAutoHealConfig.useQuery(
    { projectId: testContent?.test.projectId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testContent?.test.projectId },
  );

  const setErroredOperation = useSetAtom(erroredOperationAtom);
  const isPolishing = isStepSeparating(step.id);

  const { recorderState, updateLiveState } = useLiveStateManager();

  const { mutateAsync: getLangSmithAiTraceUrl } = api.admin.getLangSmithAiTraceUrl.useMutation();

  const { mutateAsync: addStep } = api.testStep.insert.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Step created', color: 'blue' });
    },
    onError: () => {
      notifications.show({ message: 'Error creating step', color: 'red' });
    },
  });

  const { mutateAsync: deleteSteps } = useDeleteSteps({ testContentId });
  const { mutateAsync: stepOpsToSteps } = useStepOpsToSteps({
    testContentId,
    stepId: step.id,
  });

  const { mutateAsync: updateStep } = api.testStep.update.useMutation({
    onSuccess: () => {
      notifications.show({
        message: 'Step mode updated',
      });
    },
    onError: () => {
      notifications.show({
        message: 'Failed to update step mode',
      });
    },
    onSettled: async () => {
      await Promise.allSettled([trpcUtils.testContent.get.invalidate()]);
    },
  });

  const { mutateAsync: _clearStepOperations, isPending: _clearStepOperationsRunning } =
    api.operation.clearStepOperations.useMutation({
      onSuccess: () => {
        trpcUtils.testContent.get.invalidate().catch(() => undefined);
        notifications.show({ message: 'Reference actions cleared', color: 'blue' });
      },
      onError: () => {
        notifications.show({ message: 'Error clearing reference actions', color: 'red' });
      },
    });
  const { mutateAsync: duplicateStep } = useDuplicateStep({ testContentId });

  const handleMoveCursorClick = useCallback(async () => {
    if (newStepOnMoveCursor) {
      const newStep = await addStep({
        description: '',
        order: step.stepOrder + 1,
        testContentId: step.testContentId || '',
        mode: 'AI_FALLBACK',
      });
      updateLiveState((prev) => ({
        ...prev,
        recorderCursor: { stepId: newStep.id, stepOrder: newStep.stepOrder },
      }));
    } else {
      const lastOperation = step.operations.at(-1);
      updateLiveState((prev) => ({
        ...prev,
        recorderCursor: {
          stepId: step.id,
          stepOrder: step.stepOrder,
          operation:
            lastOperation ?
              {
                id: lastOperation.id,
                order: lastOperation.order,
              }
            : undefined,
        },
      }));
    }
  }, [newStepOnMoveCursor, addStep, step.stepOrder, step.testContentId, step.operations, step.id, updateLiveState]);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const previewFromLocation = usePreviewFromLocation({ testContentId });

  const isInitialNavigationStep =
    step.stepOrder === 0 && !!step.operations.at(0)?.navigationOp && testContent?.test.testType !== 'MOBILE_APP';

  return (
    <div className="flex flex-col space-y-2 py-2">
      <Menu
        position="bottom-start"
        offset={-1}
        shadow="md"
        opened={settingsOpen}
        onOpen={() => setSettingsOpen(true)}
        onClose={() => setSettingsOpen(false)}
        disabled={disabled || readonly}
      >
        <Menu.Target>
          <Tooltip
            // This is inverted on purpose
            disabled={!(disabled || readonly)}
            label="Step settings are currently disabled"
            withArrow
          >
            <ActionIcon
              className="text-gray-700 disabled:bg-transparent disabled:opacity-50 dark:text-gray-200"
              variant="transparent"
              title="More options"
              disabled={disabled || readonly}
            >
              <IconDotsVertical size={ICON_SIZE} />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>

        <Menu.Dropdown
          className="p-2"
          w={220}
        >
          {step.mode !== 'AI_DYNAMIC' && step.mode !== 'AUTH' && (
            <div className="px-2 pt-2">
              <StepMode
                step={step}
                disabled={disabled || readonly}
                isInitialNavigationStep={isInitialNavigationStep}
                projectAutoHealConfig={projectAutoHealConfig ?? 'UI'}
              />
            </div>
          )}

          <Menu.Divider />

          {!step.skip && (
            <Menu.Item
              disabled={isBeforeCursor || disabled}
              onClick={() => previewFromLocation({ step })}
              leftSection={
                <IconPlayerPlayFilled
                  className="text-green-500"
                  size={ICON_SIZE}
                />
              }
            >
              {step.mode === 'AI_DYNAMIC' ? 'Preview to generate' : 'Preview to this step'}
            </Menu.Item>
          )}

          {!!isAdmin && !!previewExecutionId && (
            <Tooltip
              label="See the trace for this step for the last preview in LangSmith"
              withArrow
            >
              <Menu.Item
                onClick={async () => {
                  try {
                    const traceUrl = await getLangSmithAiTraceUrl({
                      executionId: previewExecutionId,
                      stepId: step.id,
                      initiator: 'recorder',
                    });
                    window.open(traceUrl, '_blank');
                  } catch (error) {
                    console.error('Failed to get LangSmith AI trace URL:', error);
                    notifications.show({
                      message: 'Failed to get LangSmith trace URL',
                      color: 'red',
                    });
                  }
                }}
                leftSection={
                  <IconBug
                    className="text-orange-500"
                    size={ICON_SIZE}
                  />
                }
              >
                View LangSmith trace
              </Menu.Item>
            </Tooltip>
          )}

          {recorderState?.executionType === 'record' && (
            <Menu.Item
              leftSection={
                <div
                  className="flex items-center justify-center"
                  style={{ width: `${ICON_SIZE}px`, height: `${ICON_SIZE}px` }}
                >
                  <div className="h-4 w-1 rounded-sm bg-red-500" />
                </div>
              }
              onClick={handleMoveCursorClick}
            >
              Move recorder cursor here
            </Menu.Item>
          )}

          <Menu.Item
            leftSection={<IconStrikethrough size={ICON_SIZE} />}
            onClick={async () => {
              await updateStep({ stepId: step.id, data: { skip: !step.skip } });
              step.operations.forEach(({ id }) => setErroredOperation((cur) => (cur?.id === id ? undefined : cur)));
            }}
          >
            {step.skip ? 'Unskip' : 'Skip'} Step
          </Menu.Item>

          <Menu.Item
            leftSection={<IconCopyPlus size={ICON_SIZE} />}
            onClick={async () => {
              await duplicateStep({ stepId: step.id, skip: step.skip });
            }}
          >
            Duplicate
          </Menu.Item>

          <Menu.Item
            leftSection={<IconComponents size={ICON_SIZE} />}
            onClick={() => {
              setSelectedStepsIds([step.id]);
              modals.open({
                title: 'Create Component',
                size: 'lg',
                children: (
                  <CreateComponentConfirmationDialog
                    selectedItemsCount={1}
                    selectedItemType="steps"
                    testContentId={testContentId}
                    onCancel={() => modals.closeAll()}
                  />
                ),
                centered: true,
              });
            }}
          >
            Create component
          </Menu.Item>

          <Menu.Item
            disabled={isPolishing || step.mode === 'AI_DYNAMIC'}
            leftSection={<IconBlocks size={ICON_SIZE} />}
            onClick={async () => await stepOpsToSteps({ stepId: step.id, enforceSplitting: true })}
          >
            Break into smaller steps
          </Menu.Item>

          {/* AI regen step prompt feature removed - it was broken and unreliable */}

          <Menu.Item
            color="red"
            leftSection={<IconTrash size={ICON_SIZE} />}
            onClick={async () => {
              modals.openConfirmModal({
                title: 'Delete Step',
                children:
                  isInitialNavigationStep ?
                    <Text>Are you sure you want to delete the initial navigation step?</Text>
                  : <Text>Are you sure you want to delete this step?</Text>,
                labels: {
                  confirm: isInitialNavigationStep ? 'Delete Initial Navigation Step' : 'Delete Step',
                  cancel: 'Cancel',
                },
                onConfirm: () => deleteSteps({ stepIds: [step.id] }),
                confirmProps: { color: 'red' },
                centered: true,
              });
            }}
          >
            Delete Step
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </div>
  );
}
